import React from 'react';
import { Box, Grid } from '@mantine/core';
import {
  PAGE_CONTENT_GUTTER_PX,
  PAGE_CONTENT_MAX_WIDTH_PX,
} from '../../../layout/pageLayoutConstants';
import {
  applyStackBreakpointToSpans,
  getResolvedGridSpans,
  resolveSectionColumns,
  stackBreakpointsProp,
} from '../../../utils/proposalSectionColumns';
import {
  APPEAR_VARIANT_CLASS,
  isAppearAnimationActive,
  normalizeAppearAnimation,
  normalizeAppearAnimationDuration,
} from '../../../utils/appearAnimations';
import { useAppearOnScroll } from '../../../hooks/useAppearOnScroll';
import { cssLengthToPx } from '../../../utils/proposalUnits';
import { isMediaPresent, MediaStack } from '../../../media';
import { withVariablePlaceholder } from '../../../media/variableMedia';
import { VariableMediaRefresh } from '../../../media/VariableImage';
import { resolveSectionGap } from '../../../utils/proposalSectionGaps';
import ProposalSectionNestedGrid from './ProposalSectionNestedGrid';
import SectionColumnDropZone from './SectionColumnDropZone';
import { columnColProps } from './sectionColumnStyles';
import styles from './ProposalSection.module.css';
import hiddenStyles from '../../../puck/hiddenBlock.module.css';

/** Opacité du voile quand le Bloc n'en a jamais réglé (0 à 1). */
export const DEFAULT_BACKGROUND_OVERLAY_OPACITY = 0.5;

/**
 * Bloc Puck — Section de contenu issue du proposal.
 * - Format récent : `sectionColumns` + DropZone Puck (grille Mantine responsive).
 * - Ancien format : `legacyNested` + `columns` (ProposalColumn imbriquées).
 */
const ProposalSection = ({
  id,
  backgroundColor = '',
  backgroundMedia = '',
  /** Cadrage du média de fond (`cover` recadre, `contain` ajuste). */
  mediaFit = 'cover',
  /** Voile coloré posé sur le média de fond ; vide = pas de voile. */
  backgroundOverlayColor = '',
  backgroundOverlayOpacity = DEFAULT_BACKGROUND_OVERLAY_OPACITY,
  paddingTop = 0,
  paddingBottom = 0,
  /** Gouttière gauche/droite. Vidé ('') = 0 = contenu pleine largeur. */
  paddingX,
  marginBottom = '',
  columnGap,
  rowGap,
  stackAtPx,
  /** Fond, marge intérieure, largeur ajustée — par id de colonne. */
  columnStyles = null,
  /** Placement des colonnes ajustées au contenu dans leur rangée. */
  columnsJustify = 'flex-start',
  isVisible = true,
  /** Ancre lisible. Posée en attribut, pas en `id` : l'id est déjà l'identité
   *  du bloc (clés de zones, verrous collaboratifs) et ne peut pas la porter. */
  anchor = '',
  appearAnimation = 'none',
  appearAnimationDuration,
  legacyNested = false,
  columns = null,
  sectionColumns = null,
  /** @deprecated */
  columnIds = null,
  /** @deprecated spans racine (1–12) */
  columnSpans: legacyRootSpans = null,
  /** @deprecated % */
  columnWidths = null,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const shownBackgroundMedia = withVariablePlaceholder(backgroundMedia, { isEditing });
  const appear = normalizeAppearAnimation(appearAnimation);
  const appearActive = isAppearAnimationActive(appear, { isEditing });
  const appearDurationMs = normalizeAppearAnimationDuration(appearAnimationDuration);
  const { ref: appearRef, visible: appearVisible } = useAppearOnScroll(appearActive);
  const appearClasses = appearActive
    ? `${styles.appear} ${styles[APPEAR_VARIANT_CLASS[appear]]} ${appearVisible ? '' : styles.appearHidden}`
    : '';
  const resolved = resolveSectionColumns({
    sectionColumns,
    columnIds,
    columnSpans: legacyRootSpans,
    columnWidths,
  });
  const { preset, nested, columnIds: colIds, columnSpans: colSpans } = resolved;
  const gridSpans = applyStackBreakpointToSpans(
    getResolvedGridSpans(colIds, colSpans),
    stackAtPx,
  );
  const gridBreakpoints = stackBreakpointsProp(stackAtPx);
  // Marge à 0 = contenu pleine largeur. Retirer la gouttière ne suffisait pas :
  // le plafond de 1200 px et ses marges auto laissaient le contenu centré, loin
  // des bords dès que l'écran est plus large.
  const gutterPx = paddingX === undefined
    ? PAGE_CONTENT_GUTTER_PX
    : (cssLengthToPx(paddingX) || 0);
  const useDropZones = !legacyNested && colIds.length > 0;
  const isNested = preset === 'nested' && nested;

  if (!isVisible && !isEditing) return null;

  return (
    <section
      id={id}
      data-anchor={anchor || undefined}
      ref={appearRef}
      className={`${styles.section} ${!isVisible ? hiddenStyles.hidden : ''} ${appearClasses}`.trim()}
      style={{
        backgroundColor: backgroundColor || undefined,
        paddingTop: paddingTop || undefined,
        paddingBottom: paddingBottom || undefined,
        marginBottom: marginBottom || undefined,
        ...(appearActive ? { '--appear-duration': `${appearDurationMs}ms` } : null),
      }}
    >
      {!isVisible && isEditing && (
        <span className={hiddenStyles.hiddenBadge}>Masqué</span>
      )}
      {/* Calque de fond posé en absolu plutôt qu'en MediaStack englobant : le
          contenu superposé d'un MediaStack est lui-même en absolu, la hauteur
          du Bloc viendrait alors du ratio du média et non de ses boxes. */}
      {isMediaPresent(shownBackgroundMedia) && (
        <div className={styles.backgroundMedia} aria-hidden="true">
          <MediaStack
            media={shownBackgroundMedia}
            mediaFit={mediaFit}
            isEditing={isEditing}
            asBackground
            fillContainer
            backgroundColor=""
          />
          {backgroundOverlayColor && (
            <div
              className={styles.backgroundOverlay}
              style={{ backgroundColor: backgroundOverlayColor, opacity: backgroundOverlayOpacity }}
            />
          )}
        </div>
      )}
      <VariableMediaRefresh blockId={id} path="backgroundMedia" media={backgroundMedia} isEditing={isEditing} />
      <Box
        w="100%"
        mx="auto"
        px={gutterPx}
        className={styles.content}
        style={{
          maxWidth: gutterPx ? `${PAGE_CONTENT_MAX_WIDTH_PX}px` : undefined,
          boxSizing: 'border-box',
        }}
      >
        {useDropZones ? (
          isNested ? (
            <ProposalSectionNestedGrid
              nested={nested}
              className={styles.columnsGrid}
              isEditing={isEditing}
              sectionId={id}
              columnGap={columnGap}
              rowGap={rowGap}
              stackAtPx={stackAtPx}
              columnStyles={columnStyles}
              columnsJustify={columnsJustify}
            />
          ) : (
            /* Mantine v8 : pas de prop gutter — columnGap/rowGap explicites.
               rowGap = espace vertical quand les colonnes s'empilent (mobile). */
            <Grid
              columnGap={resolveSectionGap(columnGap)}
              rowGap={resolveSectionGap(rowGap)}
              breakpoints={gridBreakpoints}
              justify={columnsJustify}
              className={styles.columnsGrid}
            >
              {colIds.map((colId, i) => (
                <Grid.Col key={colId} {...columnColProps(columnStyles, colId, gridSpans[i])}>
                  <SectionColumnDropZone
                    isEditing={isEditing}
                    sectionId={id}
                    colId={colId}
                    minEmptyHeight={isEditing ? 48 : 0}
                    collisionAxis="y"
                  />
                </Grid.Col>
              ))}
            </Grid>
          )
        ) : (
          <div className={styles.columnsLegacy}>{columns}</div>
        )}
      </Box>
    </section>
  );
};

export default ProposalSection;
